// Analytics tracking utility for Queen B Logistics
const API = process.env.REACT_APP_BACKEND_URL;
const GA_MEASUREMENT_ID = 'G-FGVERKYZ4Q';

// Session management
let sessionId = null;

const getSessionId = () => {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('qbl_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID ? crypto.randomUUID() : 
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      sessionStorage.setItem('qbl_session_id', sessionId);
    }
  }
  return sessionId;
};

// Detect traffic source
const getTrafficSource = () => {
  const referrer = document.referrer;
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check UTM parameters first
  if (urlParams.get('utm_source')) {
    return urlParams.get('utm_source');
  }
  
  // Check referrer
  if (!referrer || referrer.includes(window.location.hostname)) {
    return 'direct';
  }
  
  if (referrer.includes('google.')) return 'google';
  if (referrer.includes('facebook.') || referrer.includes('fb.')) return 'facebook';
  if (referrer.includes('instagram.')) return 'instagram';
  if (referrer.includes('twitter.') || referrer.includes('x.com')) return 'twitter';
  if (referrer.includes('linkedin.')) return 'linkedin';
  if (referrer.includes('youtube.')) return 'youtube';
  if (referrer.includes('tiktok.')) return 'tiktok';
  
  return 'referral';
};

// Track event to backend
const trackEvent = async (eventType, data = {}) => {
  try {
    const payload = {
      event_type: eventType,
      page: data.page || window.location.pathname,
      button_id: data.buttonId || null,
      source: data.source || getTrafficSource(),
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      session_id: getSessionId(),
      metadata: data.metadata || {}
    };

    // Send to backend
    fetch(`${API}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {}); // Silent fail

    // Also send to Google Analytics
    if (window.gtag) {
      window.gtag('event', eventType, {
        page_path: payload.page,
        button_id: payload.button_id,
        traffic_source: payload.source,
        ...data.metadata
      });
    }
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

// Public API
export const analytics = {
  // Track page view
  pageView: (page) => {
    trackEvent('page_view', { page: page || window.location.pathname });
  },

  // Track button click
  buttonClick: (buttonId, metadata = {}) => {
    trackEvent('button_click', { buttonId, metadata });
  },

  // Track conversion
  conversion: (type, metadata = {}) => {
    trackEvent('conversion', { metadata: { type, ...metadata } });
  },

  // Track specific events
  trackStarterPackClick: (tier) => {
    trackEvent('button_click', { 
      buttonId: `starter_pack_${tier}`,
      metadata: { category: 'starter_pack', tier }
    });
  },

  trackComplianceClick: (plan) => {
    trackEvent('button_click', { 
      buttonId: `compliance_${plan}`,
      metadata: { category: 'compliance', plan }
    });
  },

  trackAddOnClick: (addon) => {
    trackEvent('button_click', { 
      buttonId: `addon_${addon}`,
      metadata: { category: 'addon', addon }
    });
  },

  trackApplicationStart: () => {
    trackEvent('button_click', { 
      buttonId: 'start_application',
      metadata: { category: 'cta' }
    });
    trackEvent('conversion', { metadata: { type: 'application_start' } });
  },

  trackWaitlistJoin: () => {
    trackEvent('button_click', { 
      buttonId: 'join_waitlist',
      metadata: { category: 'cta' }
    });
    trackEvent('conversion', { metadata: { type: 'waitlist_join' } });
  },

  trackHiveAccess: () => {
    trackEvent('button_click', { 
      buttonId: 'access_hive',
      metadata: { category: 'cta' }
    });
  },

  // Track scroll depth
  trackScrollDepth: (depth) => {
    trackEvent('scroll', { metadata: { depth } });
  }
};

// Auto-track page views on route changes
export const initAnalytics = () => {
  // Initial page view
  analytics.pageView();

  // Track time on page
  let pageLoadTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
    trackEvent('page_exit', { 
      metadata: { 
        time_on_page: timeOnPage,
        page: window.location.pathname 
      }
    });
  });
};

export default analytics;
